import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'

type Course = {
  id: string
  kind: 'SelfPaced' | 'Live'
  name: string
  displayName: string
  courseCode?: string
  imageName: string
}

type DemographicBreakdown = {
  licenseType?: string
  licenseState?: string
  count: number
}

type LicenseTypeDemographics = {
  all: DemographicBreakdown[]
  active: DemographicBreakdown[]
  subscribed: DemographicBreakdown[]
  linkedService: DemographicBreakdown[]
}

type LicenseStateDemographics = {
  all: DemographicBreakdown[]
  active: DemographicBreakdown[]
  subscribed: DemographicBreakdown[]
  linkedService: DemographicBreakdown[]
}

type LicenseType = 'Nurse' | 'EMT' | 'Paramedic' | 'AEMT' | 'Other'

export interface SentimentAnalysisRequest {
  courseId: string
  licenseGroup: 'Nurse' | 'Paramedic' | 'EMT' | 'AEMT' | 'Other'
  startDate: string // ISO 8601 date string
  endDate: string // ISO 8601 date string
}

export interface JobProgress {
  timestamp: string
  phase: string
  message: string
}

export interface JobResult {
  correlationID: string
  response: string
  timestamp: string
}

export interface SentimentAnalysisResponse {
  correlationID: string
}

export interface JobStatusResponse {
  correlationID: string
  status: string
  createdAt: string
  updatedAt: string
  progress: JobProgress[]
  result: JobResult | null
}

export interface SentimentAnalysisError {
  error: string // e.g., "No survey data found for the specified parameters"
}

export interface CourseAnalyticsRequest {
  courseId: string
  licenseGroup: 'Nurse' | 'Paramedic' | 'EMT' | 'AEMT' | 'Other'
  startDate: string // ISO 8601 date string
  endDate: string // ISO 8601 date string
}

export interface LikertDataPoint {
  monthYear: string // "July 2025", "June 2025"
  topicsThoroughAndInteresting: number
  presenterGoodUnderstanding: number
  videoQualityProfessional: number
  benefitCareerGoals: number
  knowledgePositiveImpactPractice: number
  activityPositiveImpactPractice: number
}

export type LikertDataResponse = LikertDataPoint[]

export interface LearnerCountPoint {
  month: string // "July 2025", "June 2025"
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
}

export type LearnerCountsResponse = LearnerCountPoint[]

export interface AttendeeDetail {
  userID: string
  email: string
  firstName: string
  lastName: string
  confidenceScore: number
  evidence: string[]
}

export interface AttendanceBreakdown {
  veryHighConfidence: AttendeeDetail[] // 95-100% confidence
  highConfidence: AttendeeDetail[] // 85-94% confidence
  mediumConfidence: AttendeeDetail[] // 60-84% confidence
  lowConfidence: AttendeeDetail[] // 30-59% confidence
  uncertain: AttendeeDetail[] // 0-29% confidence
}

export interface SessionAttendanceRecord {
  sessionID: string
  sessionTime: string // ISO datetime string
  classID: string
  className: string
  totalAttendees: number
  attendanceBreakdown: AttendanceBreakdown
}

export interface AttendanceSummary {
  totalSessions: number
  totalUniqueAttendees: number
  averageAttendancePerSession: number
  highConfidencePercentage: number // % of attendees with 85%+ confidence
}

export interface LiveClassAttendanceRequest {
  courseCode: string
  startDate: string // ISO date: "2024-01-01"
  endDate: string // ISO date: "2024-01-31"
  licenseGroup: LicenseType
}

export interface LiveClassAttendanceResponse {
  courseCode: string
  courseName: string
  dateRange: string
  licenseGroup: LicenseType
  sessions: SessionAttendanceRecord[]
  summary: AttendanceSummary
}

export interface LiveClassTotalsRequest {
  courseCode: string
  startDate: string // ISO date: "2024-01-01"
  endDate: string // ISO date: "2024-01-31"
}

export interface LicenseGroupTotals {
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
}

export interface SessionBreakdown {
  sessionId: string
  sessionTime: string
  classId: string
  className: string
  totalAttendees: number
  licenseBreakdown: LicenseGroupTotals
}

export interface LiveClassTotalsResponse {
  courseCode: string
  courseName: string
  dateRange: string
  totalAttendees: number
  licenseBreakdown: LicenseGroupTotals
  sessionBreakdowns: SessionBreakdown[]
}

type ScribeSlice = {
  coursesStatus: Status
  courses: Course[]
  selectedCourse: string
  startDate: string | null
  endDate: string | null
  selectedLicenseTypes: LicenseType[]
  licenseTypeDemographics: LicenseTypeDemographics
  licenseStateDemographics: LicenseStateDemographics
  licenseTypeDemographicsStatus: Status
  licenseStateDemographicsStatus: Status
}

export let initialState: ScribeSlice = {
  coursesStatus: 'uninitialized',
  courses: [],
  selectedCourse: '',
  startDate: null,
  endDate: null,
  selectedLicenseTypes: [],
  licenseTypeDemographics: {
    all: [],
    active: [],
    subscribed: [],
    linkedService: [],
  },
  licenseStateDemographics: {
    all: [],
    active: [],
    subscribed: [],
    linkedService: [],
  },
  licenseTypeDemographicsStatus: 'uninitialized',
  licenseStateDemographicsStatus: 'uninitialized',
}

export let fetchCourses = createAsyncThunk('scribe/fetchCourses', () =>
  axios.get<Course[]>('/api/scribe/courses').then(pluckData),
)

export let fetchLicenseTypeDemographics = createAsyncThunk(
  'scribe/fetchLicenseTypeDemographics',
  async () => {
    let [all, active, subscribed, linkedService] = await Promise.all([
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-type/all',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-type/active',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-type/subscribed',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-type/linked-service',
        )
        .then(pluckData),
    ])
    return { all, active, subscribed, linkedService }
  },
)

export let fetchLicenseStateDemographics = createAsyncThunk(
  'scribe/fetchLicenseStateDemographics',
  async () => {
    let [all, active, subscribed, linkedService] = await Promise.all([
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-state/all',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-state/active',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-state/subscribed',
        )
        .then(pluckData),
      axios
        .get<DemographicBreakdown[]>(
          '/api/scribe/users/demographics/license-state/linked-service',
        )
        .then(pluckData),
    ])
    return { all, active, subscribed, linkedService }
  },
)

export let scribeSlice = createSlice({
  name: 'scribe',
  initialState,
  reducers: {
    coursesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.coursesStatus = action.payload
    },
    coursesChanged: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload
    },
    selectedCourseChanged: (state, action: PayloadAction<string>) => {
      state.selectedCourse = action.payload
    },
    startDateChanged: (state, action: PayloadAction<string | null>) => {
      state.startDate = action.payload
    },
    endDateChanged: (state, action: PayloadAction<string | null>) => {
      state.endDate = action.payload
    },
    selectedLicenseTypesChanged: (
      state,
      action: PayloadAction<LicenseType[]>,
    ) => {
      state.selectedLicenseTypes = action.payload
    },
    licenseTypeDemographicsStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.licenseTypeDemographicsStatus = action.payload
    },
    licenseStateDemographicsStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.licenseStateDemographicsStatus = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchLicenseTypeDemographics.pending, (state) => {
        state.licenseTypeDemographicsStatus = 'pending'
      })
      .addCase(fetchLicenseTypeDemographics.rejected, (state) => {
        state.licenseTypeDemographicsStatus = 'rejected'
      })
      .addCase(fetchLicenseTypeDemographics.fulfilled, (state, action) => {
        state.licenseTypeDemographicsStatus = 'fulfilled'
        state.licenseTypeDemographics = action.payload
      })
      .addCase(fetchLicenseStateDemographics.pending, (state) => {
        state.licenseStateDemographicsStatus = 'pending'
      })
      .addCase(fetchLicenseStateDemographics.rejected, (state) => {
        state.licenseStateDemographicsStatus = 'rejected'
      })
      .addCase(fetchLicenseStateDemographics.fulfilled, (state, action) => {
        state.licenseStateDemographicsStatus = 'fulfilled'
        state.licenseStateDemographics = action.payload
      })
  },
})

export let {
  coursesStatusChanged,
  coursesChanged,
  selectedCourseChanged,
  startDateChanged,
  endDateChanged,
  selectedLicenseTypesChanged,
  licenseTypeDemographicsStatusChanged,
  licenseStateDemographicsStatusChanged,
} = scribeSlice.actions

export let selectCoursesStatus = (state: AppRootState) =>
  state.scribe.coursesStatus
export let selectCourses = (state: AppRootState) => state.scribe.courses
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.scribe.courses.find((course) => course.id === id)

export let selectSelectedCourse = (state: AppRootState) =>
  state.scribe.selectedCourse

export let selectStartDate = (state: AppRootState) => state.scribe.startDate
export let selectEndDate = (state: AppRootState) => state.scribe.endDate
export let selectSelectedLicenseTypes = (state: AppRootState) =>
  state.scribe.selectedLicenseTypes

export let selectLicenseTypeDemographics = (state: AppRootState) =>
  state.scribe.licenseTypeDemographics
export let selectLicenseStateDemographics = (state: AppRootState) =>
  state.scribe.licenseStateDemographics
export let selectLicenseTypeDemographicsStatus = (state: AppRootState) =>
  state.scribe.licenseTypeDemographicsStatus
export let selectLicenseStateDemographicsStatus = (state: AppRootState) =>
  state.scribe.licenseStateDemographicsStatus

export default scribeSlice.reducer
